/**
 * CreateReminderButton Component - T130
 *
 * Button to create a new payment reminder for an invoice
 */
import { useState } from 'react';
import { Button, Modal, Input } from '../ui';
import { useCreateReminder, useRemindersForInvoice, reminderLevelLabels } from '../../hooks/useReminders';
import { formatCurrency } from '@armard/shared';

interface CreateReminderButtonProps {
  invoiceId: string;
  invoiceGrossTotal: number;
  disabled?: boolean;
}

// Default fees and interest rates by level
const REMINDER_DEFAULTS = {
  1: { fee: 5.0, dueDays: 14, interestRate: 5.0 },
  2: { fee: 10.0, dueDays: 10, interestRate: 8.0 },
  3: { fee: 15.0, dueDays: 7, interestRate: 12.0 },
} as const;

export function CreateReminderButton({
  invoiceId,
  invoiceGrossTotal,
  disabled = false,
}: CreateReminderButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [reminderFee, setReminderFee] = useState<number>(0);
  const [interestRate, setInterestRate] = useState<number>(5);
  const [notes, setNotes] = useState('');

  const { data: existingReminders = [] } = useRemindersForInvoice(invoiceId);
  const createReminder = useCreateReminder();

  const nextLevel = (existingReminders.length + 1) as 1 | 2 | 3;
  const canCreateReminder = nextLevel <= 3;
  const defaults = REMINDER_DEFAULTS[nextLevel] || REMINDER_DEFAULTS[1];

  const handleOpen = () => {
    setReminderFee(defaults.fee);
    setInterestRate(defaults.interestRate);
    setNotes('');
    setIsOpen(true);
  };

  const handleCreate = async () => {
    await createReminder.mutateAsync({
      invoiceId,
      reminderFee,
      interestRate,
      notes: notes || undefined,
    });
    setIsOpen(false);
  };

  const estimatedTotal = invoiceGrossTotal + reminderFee;

  return (
    <>
      <Button
        variant="secondary"
        onClick={handleOpen}
        disabled={disabled || !canCreateReminder || createReminder.isPending}
      >
        <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        {canCreateReminder
          ? `${reminderLevelLabels[nextLevel] || `${nextLevel}. Mahnung`} erstellen`
          : 'Maximale Mahnstufe erreicht'}
      </Button>

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} title="Mahnung erstellen">
        <div className="space-y-4">
          {/* Level info */}
          <div className="p-3 bg-white/5 rounded-lg">
            <p className="text-sm text-white/70">
              Sie erstellen die <strong>{reminderLevelLabels[nextLevel]}</strong> für diese Rechnung.
            </p>
          </div>

          {/* Fee */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              Mahngebühr (EUR)
            </label>
            <Input
              type="number"
              min="0"
              step="0.01"
              value={reminderFee}
              onChange={(e) => setReminderFee(parseFloat(e.target.value) || 0)}
            />
            <p className="text-xs text-white/40 mt-1">
              Empfohlen für {nextLevel}. Mahnung: {formatCurrency(defaults.fee)}
            </p>
          </div>

          {/* Interest rate */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              Verzugszinssatz (% p.a.)
            </label>
            <Input
              type="number"
              min="0"
              max="100"
              step="0.1"
              value={interestRate}
              onChange={(e) => setInterestRate(parseFloat(e.target.value) || 0)}
            />
            <p className="text-xs text-white/40 mt-1">
              Empfohlen: {defaults.interestRate}%
            </p>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-white/70 mb-1">
              Anmerkungen (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Zusätzliche Hinweise für die Mahnung..."
              className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-white/20"
              rows={3}
            />
          </div>

          {/* Summary */}
          <div className="p-3 bg-white/5 rounded-lg">
            <p className="text-sm text-white/70">
              Geschätzte Gesamtforderung: <strong>{formatCurrency(estimatedTotal)}</strong>
            </p>
            <p className="text-xs text-white/40 mt-1">
              (Zinsen werden basierend auf Verzugstagen berechnet)
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4">
            <Button variant="ghost" onClick={() => setIsOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={handleCreate} loading={createReminder.isPending}>
              Mahnung erstellen
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
